// EMP201 — monthly PAYE/UIF/SDL declaration to SARS.
// Reads the payroll_run totals for the selected month and shows the SARS-form
// fields. CSV download. Once the user files via eFiling they enter the SARS
// reference number and mark the run filed.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Download, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';

export default function EMP201() {
  const { currentOrg } = useOrg();
  const [params] = useSearchParams();
  const [year, setYear] = useState(Number(params.get('year') ?? new Date().getFullYear()));
  const [month, setMonth] = useState(Number(params.get('month') ?? new Date().getMonth() + 1));
  const [run, setRun] = useState<any | null>(null);
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    if (!currentOrg) return;
    const { data } = await supabase.from('dodobooks_payroll_runs').select('*')
      .eq('org_id', currentOrg.id).eq('period_year', year).eq('period_month', month).maybeSingle();
    setRun(data ?? null);
    setRef(data?.emp201_reference ?? '');
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentOrg, year, month]);

  async function markFiled() {
    if (!run) return;
    setBusy(true);
    await supabase.from('dodobooks_payroll_runs').update({
      status: 'finalised', emp201_filed_at: new Date().toISOString(), emp201_reference: ref || null,
    }).eq('id', run.id);
    setBusy(false); setMsg('Marked as filed.'); load();
  }

  function exportCsv() {
    if (!run) return;
    const rows = [
      ['Period', new Date(year, month - 1, 1).toLocaleString('en-ZA', { month: 'long', year: 'numeric' })],
      ['Total gross', run.total_gross],
      ['Total PAYE', run.total_paye],
      ['UIF (employee)', run.total_uif_employee],
      ['UIF (employer)', run.total_uif_employer],
      ['UIF total', Number(run.total_uif_employee) + Number(run.total_uif_employer)],
      ['SDL', run.total_sdl],
      ['Total payable to SARS', Number(run.total_paye) + Number(run.total_uif_employee) + Number(run.total_uif_employer) + Number(run.total_sdl)],
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `EMP201-${year}-${String(month).padStart(2, '0')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  const totalPayable = run ? Number(run.total_paye) + Number(run.total_uif_employee) + Number(run.total_uif_employer) + Number(run.total_sdl) : 0;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="h-page flex items-center gap-2"><FileText size={22} /> EMP201</h1>
        <p className="text-sm text-text-mute mt-1">Monthly declaration to SARS. PAYE + UIF (1% each side) + SDL (1%). Filed by the 7th of the following month.</p>
      </div>

      <div className="card flex flex-wrap gap-3 items-end">
        <div className="field !mb-0"><label>Year</label><input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} /></div>
        <div className="field !mb-0"><label>Month</label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-ZA', { month: 'long' })}</option>
            ))}
          </select>
        </div>
        {run && <button onClick={exportCsv} className="btn btn-secondary"><Download size={14} /> Export CSV</button>}
      </div>

      {!run ? (
        <div className="card text-center text-text-mute py-8">No payroll run found for this period. Create one from <strong>Payroll</strong> first.</div>
      ) : (
        <>
          <div className="card">
            <h2 className="h-section text-base mb-3">SARS EMP201 fields</h2>
            <table className="w-full text-sm">
              <tbody>
                <Row code="4101" label="PAYE" value={run.total_paye} />
                <Row code="4141" label="UIF (employer + employee)" value={Number(run.total_uif_employee) + Number(run.total_uif_employer)} />
                <Row code="4142" label="SDL" value={run.total_sdl} />
                <tr className="border-t-2 border-line">
                  <td className="px-3 py-3 font-bold">Total payable</td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-emerald">{formatMoney(totalPayable, 'ZAR')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <h2 className="h-section text-base mb-3">Mark as filed</h2>
            <p className="text-xs text-text-mute mb-2">After submitting on SARS eFiling, paste the reference number below to lock this run.</p>
            <div className="flex gap-2">
              <input placeholder="SARS reference / case number" value={ref} onChange={(e) => setRef(e.target.value)} className="flex-1" />
              <button className="btn btn-primary" onClick={markFiled} disabled={busy || !!run.emp201_filed_at}>
                <Check size={14} /> {run.emp201_filed_at ? 'Already filed' : 'Mark filed'}
              </button>
            </div>
            {run.emp201_filed_at && <div className="text-xs text-text-mute mt-2">Filed on {new Date(run.emp201_filed_at).toLocaleDateString('en-ZA')} — ref {run.emp201_reference || '—'}</div>}
            {msg && <div className="text-sm text-emerald mt-2">{msg}</div>}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ code, label, value }: { code: string; label: string; value: number }) {
  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2 font-mono text-xs text-text-mute">{code}</td>
      <td className="px-3 py-2">{label}</td>
      <td className="px-3 py-2 text-right font-mono">{formatMoney(value, 'ZAR')}</td>
    </tr>
  );
}
